<template>
  <!-- "3 Core Areas" nav. The parent keeps it sticky; once scrolled past
  ("stuck") it stretches to full width, swaps the title for the grey logo
  mark, centers the chips and shows a mini progress ring on the right.
  Phones only get the logo mark and the progress ring, stuck or not. -->
  <nav
    class="relative"
    :class="stuck ? '-mx-4' : 'max-w-5xl mx-auto'"
  >
    <!-- Looping chevrons in the side gutters, pointing in at the nav.
    Wide screens only (no gutter otherwise); hidden once stuck. -->
    <template v-if="!stuck && !phone">
      <div
        v-for="side in ['left', 'right']"
        :key="side"
        class="gutter-arrows hidden xl:flex items-center text-neutral-100 absolute top-1/2"
        :class="side === 'left' ? 'right-full mr-6' : 'left-full ml-6 flex-row-reverse'"
        aria-hidden="true"
      >
        <svg
          v-for="n in 3"
          :key="n"
          class="nav-chevron"
          :class="side === 'left' ? 'nav-chevron--right' : 'nav-chevron--left'"
          :style="{ animationDelay: `${(n - 1) * 180}ms` }"
          width="10"
          height="16"
          viewBox="0 0 10 16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path :d="side === 'left' ? 'M3 3l5 5-5 5' : 'M7 3L2 8l5 5'" />
        </svg>
      </div>
    </template>
    <div
      class="section-nav--bar border-border-normal px-3 py-2 flex items-center gap-x-3 gap-y-1.5 flex-wrap"
      :class="stuck ? 'border-b sm:px-6' : 'rounded-xl border shadow-sm'"
    >
      <!-- Left: title (grey logo mark + compact title once stuck) -->
      <div class="flex-shrink-0 flex items-center gap-2">
        <svg
          v-if="stuck || phone"
          class="text-icon-muted"
          width="20"
          height="20"
          viewBox="0 0 28 28"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M7.875 4.375H9.625V9.625H4.375V7.875C4.375 5.94125 5.94125 4.375 7.875 4.375Z" />
          <path
            opacity="0.85"
            d="M18.375 4.375H11.375V9.625H18.375V4.375Z"
          />
          <path
            opacity="0.7"
            d="M17.5 11.375H11.375V16.625H15.5C16.6046 16.625 17.5 15.7296 17.5 14.625V11.375Z"
          />
          <path
            opacity="0.2"
            d="M23.625 11.375H19.25V14.625C19.25 15.7296 20.1454 16.625 21.25 16.625H23.625V11.375Z"
          />
          <path
            opacity="0.1"
            d="M18.375 18.375H23.625V20.125C23.625 22.0588 22.0588 23.625 20.125 23.625H18.375V18.375Z"
          />
          <path
            opacity="0.2"
            d="M14.625 18.375H11.375V23.625H16.625V20.375C16.625 19.2704 15.7296 18.375 14.625 18.375Z"
          />
          <path
            opacity="0.6"
            d="M20.125 4.375C22.0588 4.375 23.625 5.94125 23.625 7.875V9.625H20.125V4.375Z"
          />
          <path
            opacity="0.85"
            d="M9.625 11.375H4.375V16.625H9.625V11.375Z"
          />
          <path
            opacity="0.6"
            d="M4.375 18.375H9.625V23.625H7.875C5.94125 23.625 4.375 22.0588 4.375 20.125V18.375Z"
          />
        </svg>
        <BaseText
          v-if="!phone"
          type="heading"
          size="overline"
          class="text-text-subdued uppercase"
        >
          {{ stuck ? `· ${compactTitle}` : title }}
        </BaseText>
      </div>

      <!-- Middle: section chips -->
      <div
        v-if="!phone"
        class="flex items-center gap-1.5 flex-wrap"
        :class="stuck ? 'flex-grow justify-center' : ''"
      >
        <button
          v-for="(section, index) in sections"
          :key="section.id"
          class="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-lg border bg-white text-left transition-colors duration-100 hover:border-border-hover hover:bg-background-normal"
          :class="section.progress.complete ? 'border-primary-green-25' : 'border-border-normal'"
          @click="scrollToSection(section)"
        >
          <div
            class="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
            :class="section.progress.complete ? 'bg-primary-green-100 text-white' : 'bg-neutral-50 text-text-subdued'"
          >
            <svg
              v-if="section.progress.complete"
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M2 6.5l2.5 2.5L10 3.5" />
            </svg>
            <BaseText
              v-else
              type="label"
              size="xs"
            >
              {{ index + 1 }}
            </BaseText>
          </div>
          <BaseText
            type="label"
            size="sm"
            class="text-text-loud min-w-0"
          >
            {{ section.navLabel }}
          </BaseText>
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued whitespace-nowrap flex-shrink-0"
          >
            {{ section.progress.done }}/{{ section.progress.total }}
          </BaseText>
        </button>
      </div>

      <!-- Right: mini progress tracker (stuck, or always on phones) -->
      <div
        v-if="stuck || phone"
        class="flex-shrink-0 flex items-center gap-1.5"
        :class="phone ? 'ml-auto' : ''"
      >
        <svg
          class="mini-ring"
          width="22"
          height="22"
        >
          <circle
            class="text-neutral-50"
            stroke="currentColor"
            :stroke-width="2.5"
            fill="transparent"
            r="8"
            cx="11"
            cy="11"
          />
          <circle
            class="mini-ring--arc text-primary-blue-100"
            stroke="currentColor"
            :stroke-width="2.5"
            stroke-linecap="round"
            fill="transparent"
            r="8"
            cx="11"
            cy="11"
            :stroke-dasharray="miniCircumference"
            :stroke-dashoffset="miniCircumference * (1 - percent / 100)"
          />
        </svg>
        <BaseText
          type="label"
          size="sm"
          class="text-text-loud"
        >
          {{ percent }}%
        </BaseText>
      </div>
    </div>
  </nav>
</template>

<script>
import posthog from 'posthog-js'

export default {
  name: 'SetupSectionNav',
  props: {
    title: {
      type: String,
      default: '3 Core Areas of Foreplay'
    },
    compactTitle: {
      type: String,
      default: '3 Core Areas'
    },
    // [{ id, navLabel, progress: { done, total, complete } }]
    sections: {
      type: Array,
      required: true
    },
    // Overall setup percent for the mini tracker
    percent: {
      type: Number,
      default: 0
    },
    // True once the bar is pinned by scrolling past it
    stuck: {
      type: Boolean,
      default: false
    },
    phone: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    miniCircumference () {
      return 2 * Math.PI * 8
    }
  },
  methods: {
    scrollToSection (section) {
      posthog.capture('Setup Section Nav Clicked', { section: section.id })
      const el = document.getElementById(`setup-section-${section.id}`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.section-nav--bar {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
}
.gutter-arrows {
  transform: translateY(-50%);
}
.nav-chevron {
  opacity: 0.3;
  animation-duration: 1.6s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}
.nav-chevron--right {
  animation-name: nav-chevron-in-right;
}
.nav-chevron--left {
  animation-name: nav-chevron-in-left;
}
@keyframes nav-chevron-in-right {
  0%, 100% { opacity: 0.3; transform: translateX(0); }
  40% { opacity: 1; transform: translateX(3px); }
}
@keyframes nav-chevron-in-left {
  0%, 100% { opacity: 0.3; transform: translateX(0); }
  40% { opacity: 1; transform: translateX(-3px); }
}
@media (prefers-reduced-motion: reduce) {
  .nav-chevron {
    animation: none;
  }
}
.mini-ring {
  transform: rotate(-90deg);
}
.mini-ring--arc {
  transition: stroke-dashoffset 350ms ease;
}
</style>
